import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, Button, Alert, CircularProgress, MenuItem,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SaveIcon from "@mui/icons-material/Save";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../constants";
import { useBrand } from "../../../contexts/BrandContext";

const INITIAL_FORM = {
    profileId: "",
    gender: "",
    birthdate: "",
};

const CustomerUpsertDialog = ({ open, onClose, onSaved }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const isValid = brandName && form.profileId.trim() !== "";

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);

        try {
            const body = {
                brand: brandName,
                profileId: form.profileId.trim(),
            };

            if (form.gender) body.gender = form.gender;
            if (form.birthdate) body.birthdate = form.birthdate;

            const res = await fetch(`${LOYALTY_API_BASE}/customers/upsert`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }

            onSaved("Client enregistré avec succès");
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setForm({ ...INITIAL_FORM });
        setError(null);
        onClose();
    };

    const fieldSx = {
        "& .MuiOutlinedInput-root": { borderRadius: 2 }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.search.gradient }}>
                        <PersonAddIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouveau Client
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.search.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip label={brandName || "Aucune marque"} size="small"
                        sx={{
                            fontWeight: 700, fontSize: 12,
                            bgcolor: brandName ? `${SECTION_COLORS.search.main}15` : "#fff0f0",
                            color: brandName ? SECTION_COLORS.search.main : "#DC2626"
                        }} />
                </Box>

                {!brandName && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Veuillez sélectionner une marque avant d'ajouter un client.
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth required
                            label="Profile ID"
                            value={form.profileId}
                            onChange={handle("profileId")}
                            size="small"
                            disabled={!brandName}
                            helperText="Identifiant unique du client (ex: email, id externe)"
                            sx={fieldSx}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth select
                            label="Genre"
                            value={form.gender}
                            onChange={handle("gender")}
                            size="small"
                            disabled={!brandName}
                            sx={fieldSx}
                        >
                            <MenuItem value=""><em>Non spécifié</em></MenuItem>
                            <MenuItem value="M">Masculin</MenuItem>
                            <MenuItem value="F">Féminin</MenuItem>
                            <MenuItem value="Other">Autre</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Date de Naissance"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={form.birthdate}
                            onChange={handle("birthdate")}
                            size="small"
                            disabled={!brandName}
                            sx={fieldSx}
                        />
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{
                        background: SECTION_COLORS.search.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.search.main}40` }
                    }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomerUpsertDialog;
